import { useState, useEffect } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../../../api/staff'
import type { AuthSession } from '../../../../types/plans'
import type { ConstructorPlan } from '../constructorTypes'

export function PublicThemePanel({
  plan,
  bootstrap,
  session,
  onReload,
}: {
  plan: ConstructorPlan
  bootstrap: TenantBootstrapModel
  session: AuthSession
  onReload: () => void
}) {
  const [theme, setTheme] = useState<'blue' | 'gold'>(plan.public_theme === 'gold' ? 'gold' : 'blue')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setTheme(plan.public_theme === 'gold' ? 'gold' : 'blue')
    setError('')
  }, [plan.public_theme])

  async function handleSave(nextTheme: 'blue' | 'gold') {
    setTheme(nextTheme)
    setSaving(true)
    setError('')
    try {
      await staffApi.updateConstructorPlan(bootstrap, session, plan.id, { public_theme: nextTheme })
      onReload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить тему')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-800">Тема публичного плана</h3>
      <p className="mt-1 text-[11px] text-slate-500">По умолчанию используется голубая тема как в ЛК.</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {(bootstrap.tenant === 'dhc'
          ? [{ id: 'blue' as const, label: 'Голубая', hint: 'По умолчанию', dot: 'bg-[#1aa8d6]' }]
          : [
              { id: 'blue' as const, label: 'Голубая', hint: 'По умолчанию', dot: 'bg-[#1aa8d6]' },
              { id: 'gold' as const, label: 'Улыбка gold', hint: 'Акцент золотом', dot: 'bg-[#c6ac4a]' },
            ]
        ).map((option) => (
          <button
            key={option.id}
            type="button"
            className={`rounded-lg border px-3 py-2 text-left text-xs transition ${
              theme === option.id
                ? 'border-[#1aa8d6] bg-sky-50 text-slate-900 ring-1 ring-sky-200'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
            onClick={() => void handleSave(option.id)}
            disabled={saving}
          >
            <span className="flex items-center gap-2 font-semibold">
              <span className={`h-2.5 w-2.5 rounded-full ${option.dot}`} />
              {option.label}
            </span>
            <span className="mt-1 block text-[10px] text-slate-500">{option.hint}</span>
          </button>
        ))}
      </div>
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
    </div>
  )
}
